import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from 'src/contexts/userContext';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Snackbar,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import { AdminUser, CommentAuthorType, TicketComment } from 'src/interfaces/TicketTypes';
import DocumentList from 'src/components/DocumentList';
import CommentFormatToolbar from './CommentFormatToolbar';
import { renderCommentBody, handleListEnter } from './commentMarkdown';
import { usePasteImage, PendingPaste } from 'src/hooks/usePasteImage';

interface CommentThreadProps {
  ticketId: number;
  comments: TicketComment[];
  onCommentAdded: () => void;
  adminUsers?: AdminUser[];
  // Delete uses an admin-only backend endpoint; partners pass false to hide it.
  canDelete?: boolean;
}

function CommentThread({
  ticketId,
  comments,
  onCommentAdded,
  adminUsers = [],
  canDelete = true
}: CommentThreadProps) {
  const intl = useIntl();
  const userCtx = useContext(UserContext);
  const currentUserId = userCtx?.currentUser?.id;
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteCandidateId, setDeleteCandidateId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [mentionedIds, setMentionedIds] = useState<number[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLInputElement | null>(null);
  const editInputRef    = useRef<HTMLInputElement | null>(null);

  const [pendingPastes, setPendingPastes] = useState<PendingPaste[]>([]);
  const [pasteError, setPasteError] = useState<'tooLarge' | 'uploadFailed' | null>(null);

  const pendingPastesRef = useRef<PendingPaste[]>([]);
  pendingPastesRef.current = pendingPastes;

  useEffect(() => {
    return () => {
      pendingPastesRef.current.forEach((p) => window.URL.revokeObjectURL(p.blobUrl));
    };
  }, []);

  const handlePasteNewComment = usePasteImage({
    mode: 'deferred',
    textareaRef: commentInputRef,
    value: body,
    onChange: setBody,
    onError: setPasteError,
    onBufferedPaste: (p) => setPendingPastes((prev) => [...prev, p])
  });

  const handlePasteEditComment = usePasteImage({
    mode: 'immediate',
    textareaRef: editInputRef,
    value: editBody,
    onChange: setEditBody,
    onError: setPasteError,
    ticketId,
    ticketCommentId: editingId ?? undefined
  });

  const MENTION_EXCLUDED_NAMES = ['inesco energy Master Admin'];

  const mentionCandidates = mentionQuery === null
    ? []
    : adminUsers
        .filter((u) =>
          !MENTION_EXCLUDED_NAMES.includes(u.name) &&
          u.name.toLowerCase().includes(mentionQuery.toLowerCase()) &&
          !mentionedIds.includes(u.id)
        )
        .slice(0, 8);

  const detectMention = (text: string, cursor: number) => {
    const upToCursor = text.slice(0, cursor);
    const atIdx = upToCursor.lastIndexOf('@');
    if (atIdx === -1) return null;
    const between = upToCursor.slice(atIdx + 1);
    if (/\s/.test(between)) return null;
    const prevChar = atIdx === 0 ? ' ' : upToCursor[atIdx - 1];
    if (!/\s|^$/.test(prevChar) && atIdx !== 0) return null;
    return { atIdx, query: between };
  };

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const text    = e.target.value;
    const cursor  = e.target.selectionStart ?? text.length;
    setBody(text);
    const match = detectMention(text, cursor);
    setMentionQuery(match ? match.query : null);

    // Drop mentioned IDs whose display names no longer appear in the body
    setMentionedIds((prev) =>
      prev.filter((uid) => {
        const u = adminUsers.find((au) => au.id === uid);
        return u ? text.includes(`@${u.name}`) : false;
      })
    );
  };

  const handleSelectMention = (userId: number, userName: string) => {
    const input  = commentInputRef.current;
    const cursor = input?.selectionStart ?? body.length;
    const match  = detectMention(body, cursor);
    if (!match) return;
    const before = body.slice(0, match.atIdx);
    const after  = body.slice(cursor);
    const token  = `@${userName} `;
    const next   = `${before}${token}${after}`;
    setBody(next);
    setMentionedIds((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
    setMentionQuery(null);
    const caret = before.length + token.length;
    setTimeout(() => {
      input?.focus();
      input?.setSelectionRange(caret, caret);
    }, 0);
  };

  const ALLOWED_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf',
    'video/mp4', 'video/quicktime', 'video/webm'
  ];
  const MAX_FILE_SIZE = 100 * 1024 * 1024;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      if (!ALLOWED_TYPES.includes(files[i].type) || files[i].size > MAX_FILE_SIZE) return;
    }
    setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sorted = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const startEdit = (comment: TicketComment) => {
    setEditingId(comment.id);
    setEditBody(comment.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody('');
  };

  const confirmDelete = async () => {
    if (deleteCandidateId == null) return;
    const id = deleteCandidateId;
    setDeletingId(id);
    try {
      await axiosConfig.delete('/DeleteTicketComment', { params: { id } });
      setDeleteCandidateId(null);
      if (editingId === id) {
        setEditingId(null);
        setEditBody('');
      }
      onCommentAdded();
    } finally {
      setDeletingId(null);
    }
  };

  const saveEdit = async (commentId: number) => {
    if (!editBody.trim()) return;
    setSavingEdit(true);
    try {
      await axiosConfig.post('/UpdateTicketComment', {
        id: commentId,
        body: editBody
      });
      setEditingId(null);
      setEditBody('');
      onCommentAdded();
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSubmit = async () => {
    if (!body.trim() && selectedFiles.length === 0 && pendingPastes.length === 0) return;
    setSubmitting(true);

    try {
      let commentId: number | undefined;
      let finalBody = body;
      if (body.trim()) {
        const activeMentionedIds = mentionedIds.filter((uid) => {
          const u = adminUsers.find((au) => au.id === uid);
          return u ? body.includes(`@${u.name}`) : false;
        });
        const res = await axiosConfig.post('/AddTicketComment', {
          ticketId,
          body,
          mentionedUserIds: activeMentionedIds
        });
        commentId = res.data?.id;
      }

      // Upload buffered pasted images, then rewrite blob: URLs to /DownloadDocument URLs
      if (pendingPastes.length > 0 && commentId) {
        setUploading(true);
        let bodyChanged = false;
        for (const paste of pendingPastes) {
          const formData = new FormData();
          formData.append('file', paste.blob);
          try {
            const res = await axiosConfig.post('/UploadDocument', formData, {
              // Attach to the comment only. Sending ticketId too would make the
              // backend store it at ticket scope, leaking it into the ticket's
              // Description attachment list.
              params: { scope: 0, ticketCommentId: commentId },
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            const docId = res.data?.id;
            if (docId) {
              const realUrl = `/DownloadDocument?id=${docId}`;
              if (finalBody.includes(paste.blobUrl)) {
                finalBody = finalBody.split(paste.blobUrl).join(realUrl);
                bodyChanged = true;
              }
            }
          } catch (err: any) {
            console.warn('[Paste] Upload failed:', err?.response?.data || err?.message);
            setPasteError('uploadFailed');
          }
          window.URL.revokeObjectURL(paste.blobUrl);
        }
        if (bodyChanged) {
          try {
            await axiosConfig.post('/UpdateTicketComment', { id: commentId, body: finalBody });
          } catch (err: any) {
            console.warn('[Paste] Body rewrite failed:', err?.response?.data || err?.message);
          }
        }
        setUploading(false);
      }

      if (selectedFiles.length > 0) {
        setUploading(true);
        for (const file of selectedFiles) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axiosConfig.post('/UploadDocument', formData, {
              // Attach to the comment when one was created; otherwise (file-only
              // submit, no comment body) fall back to ticket scope. Never send
              // both — that duplicates the file into the Description list.
              params: commentId
                ? { scope: 0, ticketCommentId: commentId }
                : { scope: 0, ticketId },
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (err: any) {
            console.warn(`[Documents] Upload failed for ${file.name}:`, err?.response?.data || err?.message);
          }
        }
        setUploading(false);
      }

      setBody('');
      setMentionedIds([]);
      setMentionQuery(null);
      setSelectedFiles([]);
      setPendingPastes([]);
      setRefreshKey((k) => k + 1);
      onCommentAdded();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <FormattedMessage id="comments" defaultMessage="Comments" />
        }
      />
      <Divider />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sorted.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="noComments"
              defaultMessage="No comments yet."
            />
          </Typography>
        )}

        {sorted.map((comment) => {
          const isAi = comment.authorType === CommentAuthorType.AiAgent;
          const canEdit = !isAi && currentUserId != null && comment.authorId === currentUserId;
          const isEditing = editingId === comment.id;
          return (
            <Box
              key={comment.id}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isAi ? 'primary.main' : 'grey.500'
                }}
              >
                {isAi ? (
                  <SmartToyIcon fontSize="small" />
                ) : (
                  <PersonIcon fontSize="small" />
                )}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
                >
                  <Typography variant="subtitle2">
                    {isAi ? 'AI' : (adminUsers.find(u => u.id === comment.authorId)?.name ?? `User #${comment.authorId ?? '?'}`)}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {new Date(comment.createdAt).toLocaleString()}
                  </Typography>
                  {comment.editedAt && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      <FormattedMessage
                        id="commentEdited"
                        defaultMessage="(edited {time})"
                        values={{ time: new Date(comment.editedAt).toLocaleString() }}
                      />
                    </Typography>
                  )}
                  {canEdit && !isEditing && (
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                      <Tooltip title={intl.formatMessage({ id: 'edit', defaultMessage: 'Edit' })}>
                        <IconButton
                          size="small"
                          onClick={() => startEdit(comment)}
                          aria-label={intl.formatMessage({ id: 'edit', defaultMessage: 'Edit' })}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canDelete && (
                        <Tooltip title={intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' })}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteCandidateId(comment.id)}
                            disabled={deletingId === comment.id}
                            aria-label={intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
                {isEditing ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                    <CommentFormatToolbar
                      textareaRef={editInputRef}
                      value={editBody}
                      onChange={setEditBody}
                      disabled={savingEdit}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      onKeyDown={(e) => handleListEnter(e, editBody, setEditBody)}
                      onPaste={handlePasteEditComment}
                      inputRef={editInputRef}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={cancelEdit}
                        disabled={savingEdit}
                      >
                        <FormattedMessage id="cancel" defaultMessage="Cancel" />
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => saveEdit(comment.id)}
                        disabled={savingEdit || !editBody.trim()}
                      >
                        <FormattedMessage id="save" defaultMessage="Save" />
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  renderCommentBody(comment.body)
                )}
                <DocumentList ticketCommentId={comment.id} refreshKey={refreshKey} />
              </Box>
            </Box>
          );
        })}

        <Divider />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <CommentFormatToolbar
              textareaRef={commentInputRef}
              value={body}
              onChange={setBody}
              disabled={submitting || uploading}
            />
            <Typography variant="caption" color="text.disabled">
              <FormattedMessage
                id="commentMarkdownHint"
                defaultMessage="Markdown: **bold**, #, ##"
              />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              placeholder={intl.formatMessage({
                id: 'mentionPlaceholder',
                defaultMessage: 'Type @ to mention a user'
              })}
              value={body}
              onChange={handleBodyChange}
              onKeyDown={(e) => handleListEnter(e, body, setBody)}
              onPaste={handlePasteNewComment}
              inputRef={commentInputRef}
            />
            <Popper
              open={mentionQuery !== null && mentionCandidates.length > 0}
              anchorEl={commentInputRef.current}
              placement="top-start"
              style={{ zIndex: 1300 }}
            >
              <ClickAwayListener onClickAway={() => setMentionQuery(null)}>
                <Paper elevation={4} sx={{ minWidth: 200, maxHeight: 240, overflowY: 'auto' }}>
                  <MenuList dense>
                    {mentionCandidates.map((u) => (
                      <MenuItem
                        key={u.id}
                        onClick={() => handleSelectMention(u.id, u.name)}
                      >
                        {u.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Paper>
              </ClickAwayListener>
            </Popper>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignSelf: 'flex-end' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,video/mp4,video/quicktime,video/webm"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || uploading}
              >
                <AttachFileIcon fontSize="small" />
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || uploading || (!body.trim() && selectedFiles.length === 0)}
              >
                <FormattedMessage id="addComment" defaultMessage="Add" />
              </Button>
            </Box>
          </Box>
          {selectedFiles.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedFiles.map((f, i) => (
                <Chip
                  key={i}
                  label={f.name}
                  size="small"
                  onDelete={() => setSelectedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                />
              ))}
            </Box>
          )}
          {uploading && <LinearProgress />}
        </Box>
      </CardContent>
      <Dialog
        open={deleteCandidateId !== null}
        onClose={() => deletingId === null && setDeleteCandidateId(null)}
      >
        <DialogTitle>
          <FormattedMessage id="deleteComment" defaultMessage="Delete comment" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage
              id="deleteCommentConfirm"
              defaultMessage="This will permanently delete the comment and any attachments. Continue?"
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteCandidateId(null)}
            disabled={deletingId !== null}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deletingId !== null}
          >
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={pasteError !== null}
        autoHideDuration={4000}
        onClose={() => setPasteError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setPasteError(null)}>
          {pasteError === 'tooLarge' ? (
            <FormattedMessage
              id="pasteImageTooLarge"
              defaultMessage="Pasted image exceeds 100 MB limit"
            />
          ) : (
            <FormattedMessage
              id="pasteImageUploadFailed"
              defaultMessage="Failed to upload pasted image"
            />
          )}
        </Alert>
      </Snackbar>
    </Card>
  );
}

export default CommentThread;
